// RBAC CONFIG
export const ROLES = {
  superadmin: { label: "Super Admin", color: "bg-red-100 text-red-700", badge: "SA" },
  unit_admin: { label: "Unit Admin", color: "bg-orange-100 text-orange-700", badge: "UA" },
  supervisor: { label: "Supervisor", color: "bg-blue-100 text-blue-700", badge: "SV" },
  operator: { label: "Operator", color: "bg-green-100 text-green-700", badge: "OP" },
  viewer: { label: "Viewer", color: "bg-gray-100 text-gray-600", badge: "VW" },
};

export const PERMS = {
  switchUnits: ["superadmin"],
  manageUsers: ["superadmin", "unit_admin"],
  manageRoles: ["superadmin"],
  editMachines: ["superadmin", "unit_admin"],
  addMachines: ["superadmin", "unit_admin"],
  transferMachines: ["superadmin"],
  deleteMachines: ["superadmin", "unit_admin"],
  editPM: ["superadmin", "unit_admin", "supervisor"],
  clearPMCell: ["superadmin", "unit_admin"],
  addHistory: ["superadmin", "unit_admin", "supervisor", "operator"],
  editHistory: ["superadmin", "unit_admin"],
  deleteHistory: ["superadmin", "unit_admin"],
  editSpares: ["superadmin", "unit_admin", "supervisor"],
  useSpares: ["superadmin", "unit_admin", "supervisor", "operator"],
  deleteSpares: ["superadmin", "unit_admin"],
  viewAll: ["superadmin", "unit_admin", "supervisor", "operator", "viewer"],
  accessSettings: ["superadmin", "unit_admin"],
  accessReports: ["superadmin", "unit_admin"],
};

export const ROLE_HIERARCHY = { superadmin: 4, unit_admin: 3, supervisor: 2, operator: 1, viewer: 0 };

export function can(user, perm) {
  if (!user) return false;
  if (user.role === "superadmin") return true;
  // Try dynamic cache from RBAC settings first
  try {
    const raw = (typeof localStorage !== "undefined") ? localStorage.getItem("roles_permissions_cache") : null;
    if (raw) {
      const map = JSON.parse(raw);
      if (map && Object.prototype.hasOwnProperty.call(map, user.role)) {
        const perms = map[user.role];
        if (Array.isArray(perms)) return perms.includes(perm);
      }
    }
  } catch (e) { /* fall through to static fallback */ }
  // Fallback: static PERMS dict (matches initial seed)
  return PERMS[perm]?.includes(user.role) || false;
}

// ---------------------------------------------------------------------------
// Maintenance type classification (mirror of backend/core/maintenance_types.py)
//
// "Description of Incident / Event" is free text. Three PLANNED types exist —
// "Scheduled PM", "Predictive Maintenance" (vendor repair / installation /
// calibration, "M/C P/D MAINT." in older records) and "Repair & Maintenance".
// EVERY other entry is a Breakdown, including faults typed by their own name
// ("Spring Broken", "Key Jam", "Motor Failure", "Electric issue").
//
// Keep these patterns AND their order in sync with the backend module; they also
// match the legacy free-text spellings present in existing records so historical
// planned work ("M/C P/M PLAN", "p/d maint.") is not counted as a failure.
// ---------------------------------------------------------------------------
export const MT = { PM: "pm", PREDICTIVE: "predictive", REPAIR: "repair_maintenance", BREAKDOWN: "breakdown" };
export const MT_LABELS = {
  pm: "Scheduled PM",
  predictive: "Predictive Maintenance",
  repair_maintenance: "Repair & Maintenance",
  breakdown: "Breakdown",
};
export const MT_BADGE = {
  pm: { text: "PM", cls: "bg-green-100 text-green-700" },
  predictive: { text: "PdM", cls: "bg-teal-100 text-teal-700" },
  repair_maintenance: { text: "R&M", cls: "bg-indigo-100 text-indigo-700" },
  breakdown: { text: "BD", cls: "bg-red-100 text-red-700" },
};

// PM, P/M, P M, P.M | PREVENTIVE | SCHEDULED / "schedulded" (legacy typo)
const MT_PM_RE = /\bP\s*[/.\-]?\s*M\b|PREVENTI|\bSCHEDUL/i;
// Predictive named outright — P/D, P D, P.D | PREDICTIVE. Must beat the R&M rule
// because "M/C P/D MAINT." contains MAINT but is predictive work.
const MT_PREDICTIVE_NAME_RE = /\bP\s*[/.\-]?\s*D\b|PREDICTI/i;
// MAINT / MAINTENANCE / "maintance" | SERVICE / SERVICING / "serviving" | AMC.
const MT_REPAIR_RE = /MAINT|SERVI[CV]|\bAMC\b/i;
// Predictive described by the work done: repair, installation, calibration, fix-up,
// problem solved. \bFIX\b must not match "FIXTURE" — that one is a breakdown.
const MT_PREDICTIVE_WORK_RE = /REPAIR|INSTALL|CALL?IBRAT|\bFIX(?:ED|ING)?\b|\bSOLV/i;

// First match wins — the order encodes the precedence.
const MT_RULES = [
  [MT.PM, MT_PM_RE],
  [MT.PREDICTIVE, MT_PREDICTIVE_NAME_RE],
  [MT.REPAIR, MT_REPAIR_RE],
  [MT.PREDICTIVE, MT_PREDICTIVE_WORK_RE],
];

export function classifyMaintenanceType(description, logType) {
  if (String(logType || "").trim().toLowerCase() === "pm") return MT.PM;
  const text = String(description || "").trim();
  if (!text) return MT.BREAKDOWN;
  for (const [type, re] of MT_RULES) {
    if (re.test(text)) return type;
  }
  return MT.BREAKDOWN;
}

// Options for the Maintenance Type selector, in reporting order.
export const MT_OPTIONS = [MT.BREAKDOWN, MT.PM, MT.PREDICTIVE, MT.REPAIR];

// The effective type of a log entry: a type a user set by hand always wins over the
// guess made from the description text.
export function maintenanceTypeOf(log) {
  const explicit = String((log || {}).maintenanceType || "").trim().toLowerCase();
  if (MT_OPTIONS.includes(explicit)) return explicit;
  return classifyMaintenanceType((log || {}).description, (log || {}).logType);
}

export function isBreakdownEntry(description, logType) {
  return classifyMaintenanceType(description, logType) === MT.BREAKDOWN;
}

// Status colors & labels
export const SC = { PLAN: "bg-blue-100 text-blue-700", DONE: "bg-green-100 text-green-700", DELAYED: "bg-yellow-100 text-yellow-700", CANCELLED: "bg-red-100 text-red-700" };
// A PLAN cell whose month has passed. Same status, different colour, so a missed PM
// is visible in the grid instead of looking identical to a future planned one.
export const SC_OVERDUE = "bg-red-100 text-red-700 ring-1 ring-red-400";
export const SL = { PLAN: "Planned", DONE: "Completed", DELAYED: "Delayed", CANCELLED: "Cancelled" };
// Unit colour themes. Class names are written out in full — Tailwind only ships classes
// it can see as complete strings, so these cannot be built by joining pieces together.
export const UC = {
  blue: "bg-blue-600", purple: "bg-purple-600", teal: "bg-teal-600",
  amber: "bg-amber-600", rose: "bg-rose-600", indigo: "bg-indigo-600", green: "bg-green-600",
};
export const UC2 = {
  blue: "bg-blue-50 text-blue-800 border-blue-200",
  purple: "bg-purple-50 text-purple-800 border-purple-200",
  teal: "bg-teal-50 text-teal-800 border-teal-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
  indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  green: "bg-green-50 text-green-800 border-green-200",
};
// Offered in the Add / Edit Unit form, in this order.
export const UNIT_COLORS = [
  { value: "blue", label: "Blue" },
  { value: "purple", label: "Purple" },
  { value: "teal", label: "Teal" },
  { value: "amber", label: "Amber" },
  { value: "rose", label: "Rose" },
  { value: "indigo", label: "Indigo" },
  { value: "green", label: "Green" },
];

export function getMonthsForFY(fy) {
  const startYear = parseInt(fy.split("-")[0]);
  const shortStart = String(startYear).slice(-2);
  const shortEnd = String(startYear + 1).slice(-2);
  return [`APR-${shortStart}`,`MAY-${shortStart}`,`JUN-${shortStart}`,`JUL-${shortStart}`,`AUG-${shortStart}`,`SEP-${shortStart}`,`OCT-${shortStart}`,`NOV-${shortStart}`,`DEC-${shortStart}`,`JAN-${shortEnd}`,`FEB-${shortEnd}`,`MAR-${shortEnd}`];
}

// Lowest to highest — Sanber Labs Unit-3 sits in a basement. This is the one list; the
// machine form, the PM Plan floor filter and the PM row editor all read it, so a floor
// added here shows up everywhere.
export const FLOORS = ["BASEMENT", "GROUND FLOOR", "1ST FLOOR", "2ND FLOOR", "3RD FLOOR"];
// A machine that has been sold is off the floor for good, like Scrapped, but the
// distinction matters for the asset register and for anyone asking where it went.
export const STATUSES_M = ["Active", "Under Maintenance", "Inactive", "Scrapped", "Sold Out"];
// Statuses meaning "not in service". These machines are not expected to carry a PM
// schedule, so PM Health must not report them as unserviced.
export const OUT_OF_SERVICE_STATUSES = ["Scrapped", "Sold Out", "Inactive"];
// Off the floor for good: hidden from the Machine List and badged wherever they still appear.
export const RETIRED_STATUSES = ["Sold Out", "Scrapped"];

// ---------------------------------------------------------------------------
// Instrument list.
//
// Instruments are the calibrated measuring / testing equipment, kept unit-wise
// beside the machine list. Types and statuses are offered as a list rather than
// free text so the filters on the Instrument List stay usable; both selects also
// accept whatever is already stored, so an older value is never silently lost.
// ---------------------------------------------------------------------------
export const INSTRUMENT_TYPES = [
  "Measuring", "Testing", "Gauge", "Weighing", "Pressure",
  "Temperature", "Electrical", "Hardness", "Other",
];
export const STATUSES_I = ["Active", "Under Calibration", "Inactive", "Scrapped"];
export const CALIBRATION_STATUSES = ["Calibrated", "Due", "Overdue", "Not Calibrated", "Not Required"];

// How near a due date counts as "Due Soon" on the list.
export const CALIBRATION_DUE_SOON_DAYS = 30;

/**
 * The calibration state an instrument is actually in, for the badge on the list.
 *
 * Derived from the due date rather than read straight from the stored status: a
 * status typed in six months ago says "Calibrated" long after the date it was
 * calibrated to has passed. "Not Required" is honoured as stored — an instrument
 * outside the calibration schedule never becomes overdue.
 */
export function calibrationState(inst, now = new Date()) {
  const stored = String(inst?.calibrationStatus || "").trim();
  if (stored === "Not Required") return { key: "none", label: "Not Required", cls: "bg-gray-100 text-gray-500", days: null };

  const due = inst?.calibrationDueDate ? new Date(inst.calibrationDueDate) : null;
  if (!due || isNaN(due.getTime())) {
    return stored === "Calibrated"
      ? { key: "ok", label: "Calibrated", cls: "bg-green-100 text-green-700", days: null }
      : { key: "unknown", label: stored || "No Due Date", cls: "bg-gray-100 text-gray-500", days: null };
  }

  const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOfDay(due) - startOfDay(now)) / 86400000);
  if (days < 0) return { key: "overdue", label: "Overdue", cls: "bg-red-100 text-red-700 ring-1 ring-red-300", days };
  if (days <= CALIBRATION_DUE_SOON_DAYS) return { key: "due", label: "Due Soon", cls: "bg-yellow-100 text-yellow-700", days };
  return { key: "ok", label: "Calibrated", cls: "bg-green-100 text-green-700", days };
}

// PM helpers shared across Dashboard and PMPlan
export function pmCellStatus(entry) {
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry.status || null;
}
// ---------------------------------------------------------------------------
// PM due list with carry-forward.
//
// A PM that was not completed in its scheduled month stays DUE — it is carried
// forward into every following month until it is marked Completed / Delayed /
// Cancelled. The PM Plan grid is untouched: the cell keeps its original month,
// this only decides what appears on the due lists.
// ---------------------------------------------------------------------------
export const MONTH_ABBRS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

// "APR-26" -> Date(2026, 3, 1). Returns null when the key cannot be parsed.
export function monthKeyToDate(key) {
  if (!key) return null;
  const [m, y] = String(key).split("-");
  const idx = MONTH_ABBRS.indexOf(String(m || "").trim().toUpperCase());
  const yr = parseInt(y, 10);
  if (idx < 0 || isNaN(yr)) return null;
  return new Date(2000 + yr, idx, 1);
}

export function currentMonthKey(now = new Date()) {
  return `${MONTH_ABBRS[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
}

export function currentFYKey(now = new Date()) {
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  return m >= 4 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`;
}

function monthsBetween(from, to) {
  if (!from || !to) return 0;
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

// True while a PM cell is still outstanding (not Completed / Delayed / Cancelled).
function isPendingStatus(status) {
  return status === "PLAN" || status === "" || status == null;
}

/**
 * Every PM still outstanding on or before `uptoMonthKey`, oldest first.
 * Returns [{ plan, month, monthDate, carriedForward, monthsLate }].
 * `fy` limits the scan to one financial year — a new FY re-schedules every
 * machine, so last year's misses are not dragged in forever.
 */
export function getDuePMs(plans, uptoMonthKey, fy) {
  const upto = monthKeyToDate(uptoMonthKey);
  if (!upto) return [];
  const out = [];
  for (const plan of plans || []) {
    if (fy && (plan.fy || "2025-26") !== fy) continue;
    for (const [month, cell] of Object.entries(plan.schedule || {})) {
      if (!isPendingStatus(pmCellStatus(cell))) continue;
      const monthDate = monthKeyToDate(month);
      if (!monthDate || monthDate > upto) continue;
      out.push({
        plan,
        month,
        monthDate,
        carriedForward: monthDate < upto,
        monthsLate: monthsBetween(monthDate, upto),
      });
    }
  }
  // Oldest first — the most overdue machine is the one to chase today.
  out.sort((a, b) => a.monthDate - b.monthDate ||
    String(a.plan.machineCode || "").localeCompare(String(b.plan.machineCode || "")));
  return out;
}

// A PM cell still at PLAN whose month has already passed.
export function isPMCellOverdue(month, status, now = new Date()) {
  if (!isPendingStatus(status)) return false;
  const d = monthKeyToDate(month);
  return !!d && d < monthKeyToDate(currentMonthKey(now));
}

export function pmCellMeta(entry) {
  if (!entry || typeof entry === "string") return {};
  return {
    completedDate: entry.completedDate,
    completedBy: entry.completedBy,
    remarks: entry.remarks,
    evidenceFile: entry.evidenceFile,
    evidenceFileName: entry.evidenceFileName,
    evidencePhotoKey: entry.evidencePhotoKey,
    workType: entry.workType,
    vendorName: entry.vendorName,
    poNumber: entry.poNumber,
    poDate: entry.poDate,
    poAmount: entry.poAmount,
    poFilePath: entry.poFilePath,
    poFileName: entry.poFileName,
    startDate: entry.startDate,
    startTime: entry.startTime,
    endDate: entry.endDate,
    endTime: entry.endTime,
  };
}

// ---------------------------------------------------------------------------
// PM check sheets: the combined 6 MONTH + 1 YEAR sheet.
//
// A machine type usually has two published sheets. The 1st PM of the year runs the
// 6 MONTH one and the 2nd runs the 1 YEAR one, so the technician picks whichever the
// job calls for. When a single visit covers both — the annual service that also does
// the six-monthly work — they had to fill one sheet, save, switch, and fill the other,
// with the switch clearing what was already typed. This builds one sheet holding the
// checkpoints of both.
//
// A job listed on both sheets appears once. Doing it twice in one visit is not the
// intent, and a duplicated row invites one of the two being left blank. Each row keeps
// the frequency it came from, so the per-row Frequency column still reads correctly and
// the printed sheet still shows which checks are six-monthly and which are annual.
// ---------------------------------------------------------------------------
export const COMBINED_SHEET_FREQ = "6 MONTH + 1 YEAR";
const COMBINED_PARTS = ["6 MONTH", "1 YEAR"];

// A check sheet is found by machine type, so a machine's name has to match a template's
// machineType — give or take case, stray spaces, and anything in brackets. One definition,
// used both where the plan looks a sheet up and where a machine is named, so the two can
// never drift apart.
//
// Brackets are a naming note, not part of the type. Two SURFACE GRINDERs on the floor need
// telling apart on the list, so one gets called "SURFACE GRINDER (HYDRAULIC)" — and that
// used to match no template at all, leaving a machine with a published check sheet showing
// none. What is inside the brackets is for the person reading the list; the sheet is
// decided by what is outside them.
//
// Only brackets are dropped, never a word on its own: HYDRAULIC PRESS, HYDRAULIC STACKER
// and HYDRAULIC HAND PALLET are each a type in their own right with their own sheet, and
// stripping the word would collapse them onto PRESS, STACKER and HAND PALLET.
export const machineTypeKey = v => String(v || "")
  .replace(/\([^)]*\)/g, " ")
  .toUpperCase().replace(/\s+/g, " ").trim();

const _freqRank = f => {
  const i = COMBINED_PARTS.indexOf(f);
  return i < 0 ? COMBINED_PARTS.length : i;
};

// The machine types that have a published check sheet, each with the documents behind it
// — what the Machine Name dropdown offers, so a machine is named something the plan can
// actually find a sheet for.
export function checkSheetTypeOptions(templates) {
  const byType = new Map();
  for (const t of templates || []) {
    const name = String(t?.machineType || "").trim();
    if (!name) continue;
    const key = machineTypeKey(name);
    if (!byType.has(key)) byType.set(key, { machineType: name, sheets: [] });
    const sheets = byType.get(key).sheets;
    // One sheet per frequency — the plan picks the first match for each, so a duplicate
    // template would otherwise pad the label with a document nobody is ever handed.
    if (sheets.some(sh => sh.frequency === (t.frequency || ""))) continue;
    sheets.push({ frequency: t.frequency || "", docNo: String(t.docNo || "").trim() });
  }
  return [...byType.values()]
    .map(o => {
      const sheets = o.sheets.sort((a, b) => _freqRank(a.frequency) - _freqRank(b.frequency));
      return { ...o, sheets, label: sheets.map(s => `${s.docNo || "—"} ${s.frequency}`).join(" / ") };
    })
    .sort((a, b) => a.machineType.localeCompare(b.machineType));
}

// Why a machine is getting no preventive maintenance this financial year, or null if it
// is scheduled. Three different states read the same on the floor — nobody is servicing
// it — so they share one badge, and the reason says which it is.
//
// Deliberately silent about retired machines: no PM is expected once something is sold
// or scrapped, so badging those would be noise on the rows that matter least.
const _planCodeKey = v => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Mirrors OUT_OF_SERVICE_STATUSES in backend/routers/reports.py, so this badge and the
// PM Health report never disagree about which machines are expected to have PM. Wider
// than RETIRED_STATUSES, which is only the two that leave the register.
const NO_PM_EXPECTED = ["scrapped", "sold", "sold out", "inactive"];

export function noScheduleReason(machine, pmPlan = [], fy = currentFYKey()) {
  if (!machine) return null;
  if (NO_PM_EXPECTED.includes(String(machine.status || "Active").trim().toLowerCase())) return null;
  const rows = (pmPlan || []).filter(p => (p.fy || "2025-26") === fy);
  const code = _planCodeKey(machine.machineNo);
  const row = rows.find(p => p.machineId && p.machineId === machine.id)
    || (code ? rows.find(p => _planCodeKey(p.machineCode) === code) : null);
  if (!row) return "No PM plan row for FY " + fy;
  if (String(row.freq || "").toUpperCase().includes("NO SCHEDULE")) return "PM frequency is NO SCHEDULE";
  if (!Object.values(row.schedule || {}).some(Boolean)) return "PM plan row has no month scheduled";
  return null;
}

// A stored "HH:MM" shown the way the floor reads it. Times are entered with an explicit
// AM/PM box now, so printing them back as 24-hour makes the two disagree: a fitter who
// chose 1:00 PM should not have to read 13:00 on the card and work out whether that is
// what they picked.
//
// Anything that is not a plain HH:MM — "", "—", or the 24:00 the downtime trail uses to
// mean end-of-day — is handed back untouched rather than mangled into a wrong time.
export function formatTime12(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? "").trim());
  if (!m) return hhmm ?? "";
  const h = Number(m[1]);
  if (h > 23) return hhmm;
  const suffix = h < 12 ? "AM" : "PM";
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${suffix}`;
}

// How far back from TODAY anyone may date a close. Mirrors CLOSE_BACKDATE_DAYS in
// backend/routers/history.py — the picker must offer exactly the dates the API accepts,
// or it hands out values that are rejected on save.
export const CLOSE_BACKDATE_DAYS = 1;

// The earliest and latest close dates a non-superadmin may pick: today, or the day
// before. Measured from today, NOT from the day the entry opened — a breakdown that ran
// from the 20th to the 24th is closed on the 24th or the 23rd. Never before it opened,
// never in the future.
export function closeDateWindow(startDate, today) {
  const d = new Date(`${today}T00:00:00`);
  if (isNaN(d.getTime())) return { min: today, max: today };
  d.setDate(d.getDate() - CLOSE_BACKDATE_DAYS);
  let lo = d.toLocaleDateString("en-CA");
  const begun = String(startDate || "").trim();
  if (begun) {
    if (begun > today) return { min: today, max: today };
    // An entry opened today cannot be closed yesterday.
    if (begun > lo) lo = begun;
  }
  return { min: lo, max: today };
}

// The check sheets a machine of this name would get, by the plan's own matching rule.
export function checkSheetsForName(templates, name) {
  if (!machineTypeKey(name)) return [];
  const hit = checkSheetTypeOptions(templates)
    .find(o => machineTypeKey(o.machineType) === machineTypeKey(name));
  return hit ? hit.sheets : [];
}

const _cpKey = cp => String(cp?.text || "").trim().toLowerCase().replace(/\s+/g, " ");

export function buildCombinedCheckSheet(templates) {
  const parts = COMBINED_PARTS
    .map(freq => (templates || []).find(t => t && t.frequency === freq))
    .filter(Boolean);
  if (parts.length < COMBINED_PARTS.length) return null;   // nothing to combine

  const seen = new Set();
  const checkpoints = [];
  for (const t of parts) {
    for (const cp of (t.checkpoints || [])) {
      const key = _cpKey(cp);
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      checkpoints.push({ ...cp, freq: cp.freq || cp.frequency || t.frequency });
    }
  }
  // One visit fills both documents, so the sheet names both numbers with the period each
  // one covers. Spreading parts[0] alone carried only the 6 MONTH number, which left the
  // annual document looking unrecorded on the sheet and in the saved history entry.
  const docNoParts = parts.map(t => ({ frequency: t.frequency, docNo: (t.docNo || "").trim() }));
  return {
    ...parts[0],
    id: parts.map(t => t.id).join("+"),
    frequency: COMBINED_SHEET_FREQ,
    docNo: docNoParts.map(p => `${p.docNo || "—"} ${p.frequency}`).join(" / "),
    docNoParts,
    checkpoints,
  };
}

// The frequencies offered inside a check sheet: the published ones, plus the combined
// sheet when both halves exist to combine.
export function sheetFrequencyOptions(templates) {
  const published = [...new Set((templates || []).map(t => t && t.frequency).filter(Boolean))].sort();
  const canCombine = COMBINED_PARTS.every(f => published.includes(f));
  return canCombine ? [...published, COMBINED_SHEET_FREQ] : published;
}
